/**
 * DviStageAnalyze - Stage 3: AI analysis controls
 * 
 * Trigger analysis on pending groups and view results.
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Sparkles, 
  Loader2, 
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  RefreshCw
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FindingGroup } from '@/hooks/useDviFindingGroups';

interface DviStageAnalyzeProps {
  findingGroups: FindingGroup[];
  pendingGroups: FindingGroup[];
  analyzedGroups: FindingGroup[];
  onAnalyzeGroup: (groupId: string) => void;
  onAnalyzeAll: () => void;
  isAnalyzing: boolean;
  analyzingGroupId?: string | null;
  canAnalyze: boolean;
  isLoading: boolean;
}

const CONDITION_CONFIG = {
  green: { label: 'Good', icon: CheckCircle, className: 'text-green-600' },
  yellow: { label: 'Monitor', icon: AlertTriangle, className: 'text-yellow-600' },
  red: { label: 'Urgent', icon: XCircle, className: 'text-red-600' },
};

export function DviStageAnalyze({
  findingGroups,
  pendingGroups,
  analyzedGroups,
  onAnalyzeGroup,
  onAnalyzeAll,
  isAnalyzing,
  analyzingGroupId,
  canAnalyze,
  isLoading,
}: DviStageAnalyzeProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (findingGroups.length === 0) {
    return (
      <div className="text-center py-8 space-y-2">
        <Sparkles className="h-12 w-12 text-muted-foreground mx-auto" />
        <p className="text-sm text-muted-foreground">No groups to analyze</p>
        <p className="text-xs text-muted-foreground">
          Create finding groups first in the Group stage
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Bulk Analyze */}
      {pendingGroups.length > 0 && canAnalyze && (
        <Button
          onClick={onAnalyzeAll}
          disabled={isAnalyzing}
          className="w-full gap-2"
        >
          {isAnalyzing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          Analyze All Pending ({pendingGroups.length})
        </Button>
      )}

      {/* Stats */}
      <div className="flex items-center gap-2 flex-wrap">
        {pendingGroups.length > 0 && (
          <Badge variant="outline" className="gap-1 bg-muted">
            <Clock className="h-3 w-3" />
            {pendingGroups.length} pending
          </Badge>
        )}
        {analyzedGroups.length > 0 && (
          <Badge variant="outline" className="gap-1 bg-green-500/10 text-green-600">
            <CheckCircle className="h-3 w-3" />
            {analyzedGroups.length} analyzed
          </Badge>
        )}
      </div>

      {/* Groups List */}
      <div className="space-y-2">
        {findingGroups.map((group) => {
          const isPending = group.status === 'pending';
          const isThisGroupAnalyzing = isAnalyzing && analyzingGroupId === group.id;
          const config = group.condition ? CONDITION_CONFIG[group.condition] : null;

          return (
            <Card key={group.id}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {config ? (
                        <config.icon className={cn('h-4 w-4', config.className)} />
                      ) : (
                        <Clock className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="text-sm font-medium truncate">
                        {group.title || 'Untitled Group'}
                      </span>
                    </div>
                    {group.summary && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                        {group.summary}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 shrink-0">
                    {group.confidence !== null && (
                      <span className={cn(
                        'text-xs',
                        group.confidence >= 0.8 ? 'text-green-600' :
                        group.confidence >= 0.6 ? 'text-yellow-600' : 'text-red-600'
                      )}>
                        {Math.round(group.confidence * 100)}%
                      </span>
                    )}
                    
                    {canAnalyze && (
                      <Button
                        size="sm"
                        variant={isPending ? 'secondary' : 'ghost'}
                        onClick={() => onAnalyzeGroup(group.id)}
                        disabled={isAnalyzing}
                        className="gap-1.5 h-7"
                      >
                        {isThisGroupAnalyzing ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : isPending ? (
                          <Sparkles className="h-3 w-3" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                        {isPending ? 'Analyze' : 'Re-run'}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Recommended jobs preview */}
                {group.recommended_jobs?.length > 0 && (
                  <div className="mt-2 pt-2 border-t">
                    <p className="text-xs text-muted-foreground">
                      {group.recommended_jobs.length} recommended job{group.recommended_jobs.length > 1 ? 's' : ''} 
                      {' • '}
                      {group.recommended_jobs.reduce((sum, j) => sum + (j.minutes || 0), 0)} min
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
